import {Alert, Anchor, Badge, Breadcrumbs, Group, Image, Paper, Stack, Text, TextInput, Title} from "@mantine/core";
import {DataTable, type DataTableSortStatus} from "mantine-datatable";
import {useMemo} from "react";
import {Link, useParams} from "react-router";
import Page from "../../components/Page/Page.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import {useSWCLocation} from "../../utils/api.ts";
import useDataTableSessionState from "../../hooks/useDataTableSessionState.ts";
import VendorLocationMap from "./VendorLocationMap";
import useVendorsData from "./useVendorsData";
import {getCategoryOrder, getVendorId, getWareCategoryColor, getWareCategoryLabel, normalizeWareCategory} from "./utils";
import type {VendorRecord, VendorWareRecord, WareCategory} from "./types";
import {LocationDetails} from "./LocationDetails.tsx";
import {TravelPlannerButton} from "../../components/TravelPlanner/TravelPlanner.tsx";
import WareImage from "./WareImage.tsx";

const MERCHANT_IMG_SIZE = 84;
const WARE_IMG_SIZE = 64;
const integerFormat = new Intl.NumberFormat();
const priceFormat = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});

interface VendorWareTableRow extends VendorWareRecord {
    id: string;
    displayName: string;
    category: WareCategory;
    typeLabel: string;
}

const sortRows = (rows: VendorWareTableRow[], sortStatus: DataTableSortStatus<VendorWareTableRow>): VendorWareTableRow[] => {
    const accessor = sortStatus.columnAccessor as keyof VendorWareTableRow;
    const sorted = [...rows].sort((a, b) => {
        const left = a[accessor];
        const right = b[accessor];

        if (typeof left === 'number' || typeof right === 'number') {
            return (typeof left === 'number' ? left : -Infinity) - (typeof right === 'number' ? right : -Infinity);
        }

        return String(left ?? '').localeCompare(String(right ?? ''));
    });

    return sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
};

const buildVendorTypeCounts = (vendor: VendorRecord | null): Record<WareCategory, number> => {
    const counts: Record<WareCategory, number> = {
        items: 0,
        droids: 0,
        creatures: 0,
        materials: 0,
        datacards: 0,
        unknown: 0,
    };

    for (const ware of vendor?.wares ?? []) {
        counts[normalizeWareCategory(ware.type)] += 1;
    }

    return counts;
};

const renderWareName = (row: VendorWareTableRow) => (
  <Stack gap="xs">
      <Text size="sm">{row.name}</Text>
      <Text size="xs" c="dimmed">{row.entityName}</Text>
  </Stack>
)

const toVendorWareRow = (ware: VendorWareRecord, index: number): VendorWareTableRow => {
    const displayName = ware.name;
    const category = normalizeWareCategory(ware.type);
    const subType = ware.subType?.trim();
    const typeLabel = subType ? `${getWareCategoryLabel(category)} / ${subType}` : getWareCategoryLabel(category);

    return {
        ...ware,
        id: `${ware.entityId ?? displayName}:${ware.price}:${ware.quantity}:${index}`,
        displayName,
        category,
        typeLabel,
    };
};

const renderVendorWareType = (row: VendorWareTableRow) => (
    <Badge color={getWareCategoryColor(row.category)} variant="light">
        {getWareCategoryLabel(row.category)}
        {row.subType ? ` / ${row.subType}` : ''}
    </Badge>
);

const renderWareImage = (row: VendorWareTableRow) => (
    <WareImage src={row.images?.small} alt={row.entityName || ''} size={WARE_IMG_SIZE} category={row.category} radius="sm" />
);

export default function VendorDetails() {
    const {vendorId} = useParams<{vendorId?: string}>();
    // const navigate = useNavigate();
    const {location: userLocation} = useSWCLocation();
    const {vendors, isPending, isError, error} = useVendorsData();
    const {
        sortStatus,
        setSortStatus,
        filters,
        setFilter,
    } = useDataTableSessionState<DataTableSortStatus<VendorWareTableRow>, {search: string}>({
        storageKey: `vendors:detail:${vendorId ?? 'unknown'}`,
        initialSortStatus: {
            columnAccessor: 'displayName',
            direction: 'asc',
        },
        initialFilters: {
            search: '',
        },
    });
    const search = typeof filters.search === 'string' ? filters.search : '';

    const vendor = useMemo(() => {
        if (!vendorId) return null;
        return vendors.find((v) => getVendorId(v) === vendorId) ?? null;
    }, [vendors, vendorId]);

    const typeCounts = useMemo(() => buildVendorTypeCounts(vendor), [vendor]);

    const wareRows = useMemo(() => {
        return (vendor?.wares ?? []).map(toVendorWareRow);
    }, [vendor]);

    const filteredRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) return wareRows;

        return wareRows.filter((row) =>
            row.entityName?.toLowerCase().includes(needle)
            || row.name.toLowerCase().includes(needle)
            || row.type.toLowerCase().includes(needle)
            || (row.subType ?? '').toLowerCase().includes(needle)
            || row.currency.toLowerCase().includes(needle)
        );
    }, [wareRows, search]);

    const sortedRows = useMemo(() => sortRows(filteredRows, sortStatus), [filteredRows, sortStatus]);

    if (isPending) {
        return <LoaderScreen message="Loading vendor details" />;
    }

    return (
        <Page>
            <title>{vendor ? `${vendor.name} Vendor` : 'Vendor Details'}</title>

            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <div style={{flex: 1, minWidth: 300}}>
                        <Breadcrumbs>
                            <Anchor component={Link} to="/vendors">Vendors</Anchor>
                            <Anchor component={Link} to={`/vendors/${vendor?.id}`}>{vendor?.name ?? 'Vendor not found'}</Anchor>
                        </Breadcrumbs>

                        <Title order={1}>{vendor?.name ?? 'Vendor not found'}</Title>

                        {vendor ? (
                            <Stack gap="md">
                                <Group gap="md" align="center">
                                    {vendor.merchantImage ? (
                                        <Image src={vendor.merchantImage} alt={vendor.merchant} w={MERCHANT_IMG_SIZE} h={MERCHANT_IMG_SIZE} radius="md" />
                                    ) : null}
                                    <div>
                                        <Text fw={600}>{vendor.merchant}</Text>
                                        <Text size="sm" c="dimmed">Owner: {vendor.ownerName || vendor.owner}</Text>
                                    </div>
                                </Group>

                                <Group gap={6}>
                                    {getCategoryOrder()
                                        .filter((category) => typeCounts[category] > 0)
                                        .map((category) => (
                                            <Badge key={category} color={getWareCategoryColor(category)} variant="light">
                                                {getWareCategoryLabel(category)} {typeCounts[category]}
                                            </Badge>
                                        ))}
                                </Group>

                                <Group gap={6} justify="space-between">
                                    <LocationDetails location={vendor.location}  />
                                    <div style={{alignSelf: 'end'}}><TravelPlannerButton label="Set travel planner" system={vendor.location?.coordinates.galaxy} space={vendor.location?.coordinates.system} surface={vendor.location?.coordinates.surface ?? null} ground={vendor.location?.coordinates.ground ?? null} /></div>
                                </Group>
                            </Stack>
                        ) : null}
                    </div>

                    <VendorLocationMap vendorLocation={vendor?.location ?? null} userLocation={userLocation} />

                </Group>

                {isError ? (
                    <Alert color="red" title="Unable to load vendor details">
                        {error instanceof Error ? error.message : 'Request failed'}
                    </Alert>
                ) : null}

                {!vendor ? (
                    <Alert color="yellow" title="Vendor not found">
                        No vendor matched the selected route.
                    </Alert>
                ) : (
                    <Paper withBorder p="md" radius="md">
                        <Group justify="space-between" mb="md" align="end">
                            <TextInput
                                label="Search wares"
                                placeholder="Ware name, type or currency"
                                value={search}
                                onChange={(e) => setFilter('search', e.currentTarget.value)}
                                style={{flex: 1, minWidth: 260}}
                            />
                            <Text c="dimmed" size="sm">
                                {integerFormat.format(sortedRows.length)}/{integerFormat.format(wareRows.length)} ware{sortedRows.length === 1 ? '' : 's'}
                            </Text>
                        </Group>

                        <DataTable<VendorWareTableRow>
                            idAccessor="id"
                            records={sortedRows}
                            // onRowClick={({record}) => navigate(record.warePath)}
                            sortStatus={sortStatus}
                            onSortStatusChange={setSortStatus}
                            columns={[
                                {accessor: 'images', title: 'Image', render: renderWareImage},
                                {accessor: 'displayName', title: 'Ware', sortable: true, render: renderWareName},
                                {
                                    accessor: 'category',
                                    title: 'Type',
                                    sortable: true,
                                    render: renderVendorWareType,
                                },
                                {
                                    accessor: 'quantity',
                                    title: 'Qty',
                                    sortable: true,
                                    render: (row) => <div style={{textAlign: 'right'}}>{integerFormat.format(row.quantity)}</div>,
                                },
                                {
                                    accessor: 'price',
                                    title: 'Price',
                                    sortable: true,
                                    render: (row) => <div style={{textAlign: 'right'}}>{priceFormat.format(row.price)}</div>,
                                },
                                {
                                    accessor: 'hasCustom',
                                    title: 'Custom',
                                    render: (row) => row.hasCustom ? <Badge color="violet" variant="light">Yes</Badge> : <Text size="sm" c="dimmed">No</Text>,
                                },
                            ]}
                        />
                    </Paper>
                )}
            </Stack>
        </Page>
    );
}
